import { jsPDF } from "jspdf";
import { fmtTL, fmtNum } from "./calc";
import { CONTACT } from "../data/site";

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve({ data: canvas.toDataURL("image/png"), w: img.width, h: img.height });
    };
    img.onerror = () => resolve(null);
    img.src = "/logo.png";
  });

export async function generateOfferPDF({ consumption, results, customer }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const green = [92, 197, 40];
  const dark = [10, 10, 10];
  const gray = [110, 110, 110];

  // Header band
  doc.setFillColor(...dark);
  doc.rect(0, 0, W, 42, "F");

  const logo = await loadLogo();
  if (logo) {
    const logoW = 55;
    const logoH = (logo.h / logo.w) * logoW;
    doc.addImage(logo.data, "PNG", 15, 21 - logoH / 2, logoW, logoH);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Renewolt Enerji", 15, 24);
  }

  doc.setTextColor(...green);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("GES TEKLIF OZETI", W - 15, 20, { align: "right" });
  doc.setTextColor(200, 200, 200);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(new Date().toLocaleDateString("tr-TR"), W - 15, 27, { align: "right" });

  let y = 56;

  // Customer info
  if (customer?.name || customer?.phone) {
    doc.setTextColor(...dark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Musteri Bilgileri", 15, y);
    doc.setDrawColor(...green);
    doc.setLineWidth(0.8);
    doc.line(15, y + 2, 55, y + 2);
    y += 9;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...gray);
    if (customer.name) { doc.text(`Ad Soyad: ${customer.name}`, 15, y); y += 6; }
    if (customer.phone) { doc.text(`Telefon: ${customer.phone}`, 15, y); y += 6; }
    y += 4;
  }

  // Input summary
  doc.setTextColor(...dark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Tuketim Ozeti", 15, y);
  doc.setDrawColor(...green);
  doc.line(15, y + 2, 45, y + 2);
  y += 9;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...gray);
  doc.text(`Gunluk tuketim: ${fmtNum(consumption.dailyKwh, 1)} kWh`, 15, y); y += 6;
  doc.text(`Aylik tuketim: ${fmtNum(consumption.monthlyKwh)} kWh`, 15, y); y += 6;
  doc.text(`Tahmini aylik fatura: ${fmtTL(consumption.monthlyBill)}`, 15, y);
  y += 12;

  // Results grid
  doc.setTextColor(...dark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Onerilen Sistem & Kazanc", 15, y);
  doc.setDrawColor(...green);
  doc.line(15, y + 2, 75, y + 2);
  y += 10;

  const rows = [
    ["Onerilen Sistem Gucu", `${fmtNum(results.systemKwp, 1)} kWp`],
    ["Gerekli Panel Sayisi", `${fmtNum(results.panelCount)} adet`],
    ["Gerekli Inverter Gucu", `${fmtNum(results.inverterKw, 1)} kW`],
    ["Tahmini Yillik Uretim", `${fmtNum(results.yearlyProduction)} kWh`],
    ["Yillik Tasarruf", fmtTL(results.yearlySavings)],
    ["Aylik Ortalama Tasarruf", fmtTL(results.monthlySavings)],
    ["Yatirim Geri Donus Suresi", `${fmtNum(results.paybackYears, 1)} yil`],
  ];

  rows.forEach((r, i) => {
    if (i % 2 === 0) {
      doc.setFillColor(245, 245, 245);
      doc.rect(15, y - 5, W - 30, 9, "F");
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...gray);
    doc.text(r[0], 18, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...dark);
    doc.text(r[1], W - 18, y, { align: "right" });
    y += 9;
  });

  y += 6;

  // Environmental box
  doc.setFillColor(240, 250, 235);
  doc.setDrawColor(...green);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, y, W - 30, 26, 2, 2, "FD");
  doc.setTextColor(...green);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Cevresel Etki", 20, y + 8);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...dark);
  doc.text(`Yillik CO2 tasarrufu: ${fmtNum(results.co2Yearly, 1)} ton`, 20, y + 15);
  doc.text(`25 yillik toplam: ${fmtNum(results.co2Lifetime)} ton  |  ~${fmtNum(results.treesEquivalent)} agac/yil`, 20, y + 21);
  y += 36;

  // Disclaimer
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  const disc = "* Bu teklif Akdeniz bolgesi gunes verilerine gore hazirlanmis tahmini bir on calismadir. Net proje icin ucretsiz kesif ve fizibilite talep edebilirsiniz.";
  doc.text(doc.splitTextToSize(disc, W - 30), 15, y);

  // Footer
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.3);
  doc.line(15, 278, W - 15, 278);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...gray);
  doc.text(`${CONTACT.email}  |  ${CONTACT.phone}  |  ${CONTACT.website}`, W / 2, 284, { align: "center" });
  doc.text(CONTACT.address, W / 2, 289, { align: "center" });

  doc.save("Renewolt-GES-Teklif.pdf");
}
